import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";

// Animations (Minimal)
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
`;

// Ultra Minimal Container (No Shadow, Just Border)
export const Container = styled.div`
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  padding: 12px;
  margin-bottom: 0px;
  animation: ${fadeIn} 0.5s ease-out;
`;

// Modern & Slick Header
export const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: px;
  padding-bottom: 24px;
  border-bottom: 1px solid #f3f4f6;
`;

export const TitleSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
`;

export const IconWrapper = styled.div<{ color: string }>`
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  margin-bottom: 0;
  background-color: ${(props) => props.color};
  color: #111827;
  border-radius: 14px;
  opacity: 1;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

// Modern Stats Grid (Cards)
export const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px; // Tighter gap for card look
  margin-bottom: 32px;
  padding-bottom: 0;
  border-bottom: none;
`;

// Slick Stat Card
export const StatCard = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  background: #f8fafc; // Slate-50/Gray-50
  padding: 20px 24px;
  border-radius: 16px;
  border: 1px solid transparent;
  transition: all 0.2s ease-in-out;

  &:hover {
    background: #f1f5f9; // Slate-100
    transform: translateY(-2px);
  }
`;

// Status (Dot Style)
export const StatusBadge = styled.span<{
  color: string;
  backgroundColor: string;
}>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #374151;

  &::before {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: ${(props) => props.color};
  }
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 80px 0;
  color: #9ca3af;
`;

export const OrderDetail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const CustomerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6b7280;
  font-size: 13px;
`;

export const DateInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #6b7280;
  font-size: 13px;
`;

export const AmountInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #111827;
  font-weight: 500;
  font-size: 13px;
`;

// Typography
export const Title = styled.h4`
  font-size: 22px;
  font-weight: 700;
  color: #111827;
  margin: 0;
  letter-spacing: -0.02em;
  line-height: 1.2;
`;

export const Subtitle = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
  font-weight: 400;
`;

export const StatTitle = styled.p`
  font-size: 12px;
  color: #64748b; // Slate-500
  margin: 0;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

export const StatValue = styled.h5`
  font-size: 26px;
  font-weight: 700;
  color: #0f172a; // Slate-900
  margin: 0;
  letter-spacing: -0.02em;
  line-height: 1.2;
`;

export const OrderId = styled.h6`
  font-size: 14px;
  font-weight: 500;
  color: #111827;
  margin: 0;
`;

export const OrderMeta = styled.p`
  font-size: 12px;
  color: #6b7280;
  margin: 0;
`;

export const EmptyIcon = styled.div`
  font-size: 40px;
  margin-bottom: 16px;
  opacity: 0.3;
  filter: grayscale(100%);
`;

export const EmptyTitle = styled.h6`
  font-size: 16px;
  font-weight: 500;
  color: #111827;
  margin: 0 0 8px 0;
`;

export const EmptyText = styled.p`
  font-size: 14px;
  color: #9ca3af;
  margin: 0 auto;
  max-width: 300px;
  line-height: 1.6;
`;

export const HeaderTitle = styled.h5`
  font-size: 16px; // Smaller secondary title
  font-weight: 500;
  color: #111827;
  margin: 0;
  text-align: right;
`;

export const HeaderSubtext = styled.p`
  font-size: 13px;
  color: #6b7280;
  margin: 2px 0 0 0;
  text-align: right;
`;

export const FilterWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
  margin-right: 24px;
`;

export const FilterSelect = styled.select`
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background-color: #ffffff;
  font-size: 14px;
  color: #374151;
  outline: none;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #d1d5db;
  }

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
`;

export const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s;
  height: 38px; /* Match select height if possible */

  &:hover {
    background-color: #f9fafb;
    border-color: #d1d5db;
  }
`;

// --- Updated Ultra-Minimal Table Components ---

export const OrderIdLink = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #111827;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
    opacity: 0.8;
  }
`;

export const DateTimeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
`;

export const DateText = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 6px;

  &::before {
    content: "📅";
    font-size: 12px;
    opacity: 0.8;
    filter: grayscale(1);
  }
`;

export const TimeText = styled.span`
  font-size: 11px;
  color: #6b7280;
  background: #f3f4f6;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 500;
  display: inline-block;
  margin-left: 20px; // Align with text start (icon width + gap)
`;

export const CustomerInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

export const CustomerName = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #111827;
`;

export const CustomerPhone = styled.span`
  font-size: 12px;
  color: #9ca3af;
`;

export const StoreText = styled.span`
  font-size: 13px;
  color: #374151;
`;

export const QuantityText = styled.span`
  font-size: 13px;
  color: #374151;
  text-align: center;
  display: block;
`;

export const AmountWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: flex-start;
`;

export const AmountText = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #111827;
`;

export const PaymentStatus = styled.span<{ isPaid: boolean }>`
  font-size: 11px;
  color: ${(props) => (props.isPaid ? "#059669" : "#d97706")};
  display: flex;
  align-items: center;
  gap: 4px;

  &::before {
    content: "";
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background-color: currentColor;
  }
`;

export const OrderStatusWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start; // Align left
`;

// Status Pill -> Minimal Dot Style
// Status Pill -> Badges with solid background
export const StatusPill = styled.span<{
  status: string;
}>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 99px;
  text-transform: capitalize;
  transition: all 0.2s;

  &::before {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: currentColor;
    opacity: 0.8;
  }

  ${(props) => {
    switch (props.status) {
      case "Order Placed":
        // Amber
        return `
          color: #b45309; 
          background-color: #fffbeb; 
          border: 1px solid #fcd34d;
        `;
      case "Order Confirmed":
        // Emerald
        return `
          color: #047857; 
          background-color: #ecfdf5; 
          border: 1px solid #6ee7b7;
        `;
      case "Order Processing":
        // Blue
        return `
          color: #1d4ed8; 
          background-color: #eff6ff; 
          border: 1px solid #93c5fd;
        `;
      case "Delivery Assigned":
        // Violet
        return `
          color: #6d28d9; 
          background-color: #f5f3ff; 
          border: 1px solid #c4b5fd;
        `;
      case "Ready for Pickup":
        // Cyan
        return `
          color: #0e7490; 
          background-color: #ecfeff; 
          border: 1px solid #67e8f9;
        `;
      case "Order Picked Up":
        // Orange
        return `
          color: #c2410c; 
          background-color: #fff7ed; 
          border: 1px solid #fdba74;
        `;
      case "Out for Delivery":
        // Yellow/Amber darker
        return `
          color: #a16207; 
          background-color: #fefce8; 
          border: 1px solid #fde047;
        `;
      case "Near Customer":
        // Green
        return `
          color: #15803d; 
          background-color: #f0fdf4; 
          border: 1px solid #86efac;
        `;
      case "Delivered":
        // Green Darker
        return `
          color: #14532d; 
          background-color: #dcfce7; 
          border: 1px solid #86efac;
        `;
      case "Cancelled":
        // Red
        return `
          color: #b91c1c;
          background-color: #fef2f2;
          border: 1px solid #fca5a5;
        `;
      case "Rejected":
        // Rose
        return `
          color: #9f1239;
          background-color: #fff1f2;
          border: 1px solid #fda4af;
        `;
      default:
        // Gray
        return `
          color: #374151; 
          background-color: #f9fafb; 
          border: 1px solid #e5e7eb;
        `;
    }
  }}
`;

export const DeliveryType = styled.span`
  font-size: 11px;
  color: #9ca3af;
  margin-left: 14px; // Align with text start (6px dot + 8px gap)
`;

export const ActionsWrapper = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: center;
`;

export const ActionButton = styled.button<{
  variant?: "view" | "print" | "chat";
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 4px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #9ca3af;
  transition: all 0.2s ease;

  &:hover {
    color: #111827;
    background-color: #f3f4f6;
  }

  ${(props) => {
    switch (props.variant) {
      case "view":
        return `&:hover { color: #ea580c; background-color: #fff7ed; }`;
      case "print":
        return `&:hover { color: #16a34a; background-color: #f0fdf4; }`;
      case "chat":
        return `&:hover { color: #2563eb; background-color: #eff6ff; }`;
    }
  }}
`;

export const TableHeaderWrapper = styled.div`
  padding: 0 0 24px 0;
`;

export const TableTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const TableTitleIcon = styled.span`
  font-size: 20px;
  opacity: 0.8;
`;

export const TableTitleText = styled.h3`
  font-size: 16px;
  font-weight: 500;
  color: #111827;
  margin: 0;
`;

export const TableTitleCount = styled.span`
  color: #9ca3af;
  font-size: 13px;
  font-weight: 400;
  border: 1px solid #e5e7eb;
  padding: 1px 6px;
  border-radius: 4px;
`;

// Profile Card (Ultra Clean)
export const ProfileCard = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 0;
  background: transparent;
  border: none;
`;

export const ProfileAvatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  background-color: #f3f4f6;
`;

export const ProfileAvatarPlaceholder = styled.div<{
  variant?: "customer" | "store";
}>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 500;
  ${(props) =>
    props.variant === "store"
      ? `background: #f0fdfa; color: #0d9488;` // Teal-50
      : `background: #eef2ff; color: #4f46e5;`}// Indigo-50
`;

export const ProfileDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  justify-content: center;
`;

export const ProfileName = styled.span<{ clickable?: boolean }>`
  font-size: 13px;
  font-weight: 500;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: ${(props) => (props.clickable ? "pointer" : "default")};

  &:hover {
    ${(props) => props.clickable && `text-decoration: underline;`}
  }
`;

export const ProfileSubtext = styled.span`
  font-size: 11px;
  color: #9ca3af;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;
`;

export const ProfilePhone = styled.span`
  font-size: 11px;
  color: #9ca3af;
  text-align: start;
  margin-top: 2px;
`;

// Serial Number Badge (Replaced with simple text)
export const SerialBadge = styled.span`
  font-size: 12px;
  color: #9ca3af;
  font-family: monospace;
`;

// Quantity Badge
export const QuantityBadge = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #111827;
`;

// Amount Display
export const AmountDisplay = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
`;

export const AmountValue = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #111827;
  font-variant-numeric: tabular-nums;
`;

export const PaymentBadge = styled.span<{ isPaid: boolean }>`
  font-size: 11px;
  color: ${(props) => (props.isPaid ? "#059669" : "#d97706")};
`;

// Loading Skeleton (Subtle)
export const SkeletonBox = styled.div`
  background: #f9fafb;
  background-image: linear-gradient(
    to right,
    #f9fafb 0%,
    #f3f4f6 20%,
    #f9fafb 40%,
    #f9fafb 100%
  );
  background-repeat: no-repeat;
  background-size: 800px 100%;
  animation: ${shimmer} 1.2s infinite linear;
  border-radius: 4px;
`;

export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 64px 0;
  align-items: center;
  justify-content: center;
`;

export const LoadingSpinner = styled.div`
  width: 24px;
  height: 24px;
  border: 2px solid #e5e7eb;
  border-top-color: #111827; // Dark
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const LoadingText = styled.p`
  font-size: 13px;
  color: #9ca3af;
  margin: 0;
`;
